import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  makeStyles
} from '@material-ui/core'
import GlobalInput from './form/TextInput'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '9px',
      overflow: 'hidden',
      maxWidth: '600px'
    }
  },
  dialogContent: {
    padding: '0 16px 8px'
  },
  canvasContainer: {
    position: 'relative'
  },
  signatureCanvas: {
    width: '100%',
    height: 147,
    backgroundColor: theme.colors.signatureCanvas,
    borderRadius: '5px',
    touchAction: 'none'
  },
  clearButton: {
    color: theme.colors.grey,
    fontSize: '20px',
    position: 'absolute',
    right: '0px',
    top: '0px',
    zIndex: 1,
    minWidth: 'unset'
  },
  inputsContainer: {
    marginTop: theme.spacing(1)
  },
  errorText: {
    color: theme.colors?.errorColor,
    fontSize: '12px',
    marginTop: '4px'
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    columnGap: '16px',
    width: '100%',
    padding: '0 8px',
    marginBottom: '15px'
  },
  cancelButton: {
    borderRadius: '44px',
    fontSize: '14.29px',
    padding: '10px 30px',
    fontWeight: 'bold',
    minWidth: '90px',
    height: '42px',
    color: theme.colors?.dialog?.text_grey,
    backgroundColor: theme.colors?.grey_2,
    '&:hover': {
      backgroundColor: theme.colors?.basicDisabledButtonBackground
    },
    textTransform: 'none'
  },
  continueButton: {
    borderRadius: '44px',
    fontSize: '14.29px',
    textTransform: 'none',
    padding: '10px 30px',
    fontWeight: 'bold',
    minWidth: '90px',
    height: '42px',
    color: theme.colors?.dialog?.text_white,
    border: `0.5px solid ${theme.colors?.dialog?.ok_button}`,
    backgroundColor: theme.colors?.dialog?.ok_button,
    '&:hover': {
      backgroundColor: theme.colors?.dialog?.hover,
      borderColor: theme.colors?.dialog?.hover
    },
    '&:disabled': {
      backgroundColor: theme.colors?.grey_2,
      color: theme.colors?.text_grey
    }
  }
}))

const SignatureCanvas = ({ onSignatureChange }) => {
  const classes = useStyles()
  const canvasRef = useRef(null)
  const [isDrawing, setIsDrawing] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')
    context.lineWidth = 2.5
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.strokeStyle = '#000000'

    // Set canvas dimensions to match displayed size
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width
      canvas.height = rect.height
      // Clear and reset context after resize
      context.lineWidth = 2.5
      context.lineCap = 'round'
      context.lineJoin = 'round'
      context.strokeStyle = '#000000'

      if (onSignatureChange) {
        onSignatureChange(null)
      }
    }

    resize()
    window.addEventListener('resize', resize)

    return () => {
      window.removeEventListener('resize', resize)
    }
  }, [onSignatureChange])

  const startDrawing = (e) => {
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top

    context.beginPath()
    context.moveTo(x, y)
    setIsDrawing(true)
  }

  const draw = (e) => {
    if (!isDrawing) return

    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    const rect = canvas.getBoundingClientRect()
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top

    context.lineTo(x, y)
    context.stroke()
  }

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = canvasRef.current
      const context = canvas.getContext('2d')
      context.closePath()
      setIsDrawing(false)

      // Notify parent component
      if (onSignatureChange) {
        onSignatureChange(canvas.toDataURL())
      }
    }
  }

  const clear = () => {
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')
    context.clearRect(0, 0, canvas.width, canvas.height)

    // Notify parent component
    if (onSignatureChange) {
      onSignatureChange(null)
    }
  }

  return (
    <Box className={classes.canvasContainer}>
      <canvas
        ref={canvasRef}
        className={classes.signatureCanvas}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <Button className={classes.clearButton} onClick={clear} disableRipple={true}>
        <FontAwesomeIcon icon="fa-light fa-circle-xmark" />
      </Button>
    </Box>
  )
}

const SignatureModal = ({ open, onClose, onSign }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [fullName, setFullName] = useState('')
  const [title, setTitle] = useState('')
  const [signature, setSignature] = useState(null)

  // Reset form when dialog is opened
  useEffect(() => {
    if (open) {
      setFullName('')
      setTitle('')
      setSignature(null)
    }
  }, [open])

  const handleSubmit = () => {
    if (fullName.trim() && title.trim() && signature) {
      const signatureData = {
        fullName,
        title,
        signature
      }
      onSign(signatureData)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogContent className={classes.dialogContent}>
        <Box>
          <SignatureCanvas onSignatureChange={setSignature} />
        </Box>

        <Grid container spacing={2} className={classes.inputsContainer}>
          <Grid item xs={12} sm={6}>
            <GlobalInput
              label={t('company_profile.labels.full_name')}
              required
              placeholder={t('company_profile.placeholder.full_name')}
              value={fullName}
              onChange={(value) => setFullName(value)}
              error={!fullName.trim() && fullName !== ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GlobalInput
              label={t('company_profile.labels.title')}
              required
              placeholder={t('company_profile.placeholder.title')}
              value={title}
              onChange={(value) => setTitle(value)}
              error={!title.trim() && title !== ''}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box className={classes.buttonsContainer}>
          <Button
            className={classes.cancelButton}
            onClick={onClose}
          >
            {t('general.labels.cancel')}
          </Button>
          <Button
            className={classes.continueButton}
            onClick={handleSubmit}
            disabled={!fullName || !title || !signature}
          >
            {t('company_profile.continue')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default SignatureModal
